import {
  HttpApi,
  HttpApiClient,
  HttpClient,
  HttpClientRequest,
} from "@effect/platform";
import { browserSafeFetchHttpClientLayer } from "@redotech/effect-api-shared/http/browser-safe-fetch-http-client";
import { Api } from "@redotech/effect-api-shared/rpc/api";
import { Effect, Layer, ManagedRuntime } from "effect";
import {
  Context,
  createContext,
  memo,
  ReactNode,
  useEffect,
  useMemo,
} from "react";

// Named client type keyed off Api's groups; deriving it from the make() effect's
// inferred success overflows the .d.ts serialization limit (TS7056) as Api grows.
export type ApiClient =
  typeof Api extends HttpApi.HttpApi<infer _Id, infer Groups, infer E, infer _R>
    ? HttpApiClient.Client<Groups, E, never>
    : never;

export interface EffectApiContext {
  runPromise: <A>(
    effect: Effect.Effect<A, never, HttpClient.HttpClient>,
  ) => Promise<A>;

  getClient: () => ApiClient;
}

export const EffectApiClientContext: Context<EffectApiContext | undefined> =
  createContext<EffectApiContext | undefined>(undefined);

EffectApiClientContext.displayName = "EffectApiClientContext";

export interface EffectApiClientProviderProps {
  children: ReactNode;
  baseUrl: string;
  authToken: string | undefined;
}

function createHttpClientLayer(authToken: string) {
  return Layer.effect(
    HttpClient.HttpClient,
    Effect.gen(function* () {
      const client = yield* HttpClient.HttpClient;
      return client.pipe(
        HttpClient.mapRequest(
          HttpClientRequest.setHeader("Authorization", `Bearer ${authToken}`),
        ),
      );
    }),
  ).pipe(Layer.provide(browserSafeFetchHttpClientLayer));
}

export const EffectApiClientProvider = memo(function EffectApiClientProvider({
  children,
  baseUrl,
  authToken,
}: EffectApiClientProviderProps) {
  const context = useMemo(():
    | (EffectApiContext & { dispose: () => Promise<void> })
    | undefined => {
    if (!authToken) {
      return undefined;
    }

    const httpClientLayer = createHttpClientLayer(authToken);
    const runtime = ManagedRuntime.make(httpClientLayer);

    let client: ApiClient | null = null;

    return {
      runPromise: <A,>(
        effect: Effect.Effect<A, never, HttpClient.HttpClient>,
      ) => runtime.runPromise(effect),

      getClient: () => {
        if (!client) {
          client = runtime.runSync(HttpApiClient.make(Api, { baseUrl }));
        }
        return client;
      },

      dispose: () => runtime.dispose(),
    };
  }, [baseUrl, authToken]);

  useEffect(() => {
    return () => {
      void context?.dispose();
    };
  }, [context]);

  const value = useMemo(
    () =>
      context
        ? { runPromise: context.runPromise, getClient: context.getClient }
        : undefined,
    [context],
  );

  return (
    <EffectApiClientContext.Provider value={value}>
      {children}
    </EffectApiClientContext.Provider>
  );
});

export function hasErrorTag<T extends string>(
  error: unknown,
  tag: T,
): error is { _tag: T } {
  return (
    error !== null &&
    typeof error === "object" &&
    "_tag" in error &&
    error._tag === tag
  );
}
